import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axiosInstance from '../../actions/utils/axiosInstance';
import { useAuth } from '../../contexts/AuthContext';
import { usePaddle } from '../../contexts/PaddleContext';

enum SubscriptionStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  TRIALING = 'trialing',
}

interface SubscriptionData {
  status: SubscriptionStatus;
  tier: string;
  currentBillingPeriodStart?: string;
  currentBillingPeriodEnd?: string;
  nextBilledAt?: string;
  scheduledChange?: { action: string; effective_at: string } | null;
  paddleSubscriptionId: string;
  createdAt: string;
}

interface TransactionData {
  id: string;
  status: string;
  createdAt: string;
  billedAt?: string;
  total?: string;
  currency?: string;
  invoiceNumber?: string;
}

const STATUS_LABELS: Record<SubscriptionStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  [SubscriptionStatus.ACTIVE]: { label: 'Active', color: 'success' },
  [SubscriptionStatus.PAST_DUE]: { label: 'Past Due', color: 'warning' },
  [SubscriptionStatus.PAUSED]: { label: 'Paused', color: 'info' },
  [SubscriptionStatus.CANCELED]: { label: 'Canceled', color: 'error' },
  [SubscriptionStatus.TRIALING]: { label: 'Trial', color: 'info' },
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrency(amount?: string, currency?: string): string {
  if (!amount) return '—';
  const num = parseInt(amount, 10) / 100;
  const code = (currency || 'ZAR').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(num);
  } catch {
    return `${code} ${num.toFixed(2)}`;
  }
}

const TRANSACTION_STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  completed: 'success',
  billed: 'success',
  ready: 'info',
  past_due: 'warning',
  canceled: 'error',
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshStatus, user } = useAuth();
  const { paddle } = usePaddle();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [simulating, setSimulating] = useState(false);

  const isDev = process.env.NODE_ENV === 'development';

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await axiosInstance.get<{ success: boolean; data: { subscription: SubscriptionData | null } }>(
        '/subscription',
      );
      setSubscription(res.data?.data?.subscription ?? null);
    } catch {
      setError('Failed to load subscription details.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await axiosInstance.get<{ success: boolean; data: { transactions: TransactionData[] } }>(
        '/subscription/transactions',
      );
      setTransactions(res.data?.data?.transactions ?? []);
    } catch {
      // Transactions are supplementary; don't block the page on failure
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
    fetchTransactions();
  }, [fetchSubscription, fetchTransactions]);

  const handleCancelSubscription = async () => {
    setCanceling(true);
    setError(null);
    try {
      await axiosInstance.post('/subscription/cancel');
      setCancelDialogOpen(false);
      setSuccessMsg('Subscription scheduled for cancellation at end of billing period.');
      await fetchSubscription();
      await refreshStatus();
    } catch {
      setError('Failed to cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const handleUpdatePayment = async () => {
    setUpdatingPayment(true);
    setError(null);
    try {
      const res = await axiosInstance.post<{ success: boolean; data: { transactionId: string } }>(
        '/subscription/update-payment',
      );
      const transactionId = res.data?.data?.transactionId;
      if (transactionId && paddle) {
        paddle.Checkout.open({
          transactionId,
          settings: { theme: 'dark' },
        });
      }
    } catch {
      setError('Failed to open payment update. Please try again.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const alternateTier = subscription?.tier === 'pro' ? 'basic' : 'pro';

  const handleChangePlan = async () => {
    setChangingPlan(true);
    setError(null);
    try {
      await axiosInstance.post('/subscription/change-plan', { tier: alternateTier });
      setChangePlanDialogOpen(false);
      setSuccessMsg(`Plan changed to ${alternateTier}. Changes may take a moment to reflect.`);
      await fetchSubscription();
      await fetchTransactions();
      await refreshStatus();
    } catch {
      setError('Failed to change plan. Please try again.');
    } finally {
      setChangingPlan(false);
    }
  };

  const handleSimulateWebhook = async (tier: string) => {
    setSimulating(true);
    setError(null);
    try {
      await axiosInstance.post('/dev/simulate-webhook', { tier });
      await refreshStatus();
      await fetchSubscription();
      await fetchTransactions();
      setSuccessMsg(`Simulated ${tier} subscription created.`);
    } catch {
      setError('Failed to simulate webhook.');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subscription) {
    const isAdminApproved = user?.isApproved && !user?.subscriptionTier;
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 600, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
          Subscription
        </Typography>
        <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {isAdminApproved ? (
              <>
                <Chip label="Admin Approved" color="success" size="small" sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  You have full access
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Your account was approved by an administrator. You can also subscribe to a paid plan
                  for continued access and premium features.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No Active Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Subscribe to a plan to unlock all features.
                </Typography>
              </>
            )}
            <Button
              variant="contained"
              onClick={() => navigate('/pricing')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View Plans
            </Button>
            {isDev && (
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={simulating}
                  onClick={() => handleSimulateWebhook('basic')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {simulating ? <CircularProgress size={16} /> : 'Dev: Simulate Basic'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={simulating}
                  onClick={() => handleSimulateWebhook('pro')}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {simulating ? <CircularProgress size={16} /> : 'Dev: Simulate Pro'}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  const statusInfo = STATUS_LABELS[subscription.status] ?? { label: subscription.status, color: 'info' as const };
  const hasPendingCancel = subscription.scheduledChange?.action === 'cancel';
  const isCanceled = subscription.status === SubscriptionStatus.CANCELED;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Subscription
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Plan details */}
      <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {subscription.tier} Plan
            </Typography>
            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1.5}>
            <Row label="Current Period" value={`${formatDate(subscription.currentBillingPeriodStart)} — ${formatDate(subscription.currentBillingPeriodEnd)}`} />
            <Row label="Next Billing" value={formatDate(subscription.nextBilledAt)} />
            <Row label="Subscribed Since" value={formatDate(subscription.createdAt)} />
          </Stack>

          {hasPendingCancel && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your subscription is scheduled to cancel on{' '}
              {formatDate(subscription.scheduledChange?.effective_at)}.
              You will retain access until that date.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} flexWrap="wrap">
            {!isCanceled && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setChangePlanDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Switch to {alternateTier === 'pro' ? 'Pro' : 'Basic'}
              </Button>
            )}
            {!isCanceled && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleUpdatePayment}
                disabled={updatingPayment}
                sx={{ textTransform: 'none' }}
              >
                {updatingPayment ? <CircularProgress size={18} /> : 'Update Payment Method'}
              </Button>
            )}
            {!hasPendingCancel && !isCanceled && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setCancelDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Cancel Subscription
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Billing history */}
      {transactions.length > 0 && (
        <Card sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Billing History
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{formatDate(tx.billedAt || tx.createdAt)}</TableCell>
                      <TableCell>{formatCurrency(tx.total, tx.currency)}</TableCell>
                      <TableCell>
                        <Chip
                          label={tx.status.replace('_', ' ')}
                          size="small"
                          color={TRANSACTION_STATUS_COLOR[tx.status] || 'default'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        {tx.invoiceNumber || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Cancel dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your subscription will remain active until the end of the current billing period.
            After that, your access will be revoked. You can resubscribe at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={canceling}>
            Keep Subscription
          </Button>
          <Button onClick={handleCancelSubscription} color="error" disabled={canceling}>
            {canceling ? <CircularProgress size={20} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change plan dialog */}
      <Dialog open={changePlanDialogOpen} onClose={() => setChangePlanDialogOpen(false)}>
        <DialogTitle>Change Plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {alternateTier === 'pro'
              ? 'Upgrade to the Pro plan for unlimited jobs and premium features. The price difference will be prorated.'
              : 'Downgrade to the Basic plan. The price difference will be prorated on your next billing cycle.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePlanDialogOpen(false)} disabled={changingPlan}>
            Keep Current Plan
          </Button>
          <Button onClick={handleChangePlan} variant="contained" disabled={changingPlan}>
            {changingPlan ? <CircularProgress size={20} /> : `Switch to ${alternateTier === 'pro' ? 'Pro' : 'Basic'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Stack>
);

export default SubscriptionPage;
